/**
 * Summarize a node's current configuration in a single-line sentence for the
 * node card. Heuristic, not schema-driven, because the meaningful summary
 * depends on the primitive (e.g. EXTRACT summarizes its entity chips).
 */

import type { DefinitionNode } from '~/pages/workflows/builder/types';

export function summarizeConfig(node: DefinitionNode): string | null {
  switch (node.taskKey) {
    case 'INPUT_DOCUMENT': {
      const ocr = node.config.ocrMode as string | undefined;
      return `Document · OCR ${ocr ?? 'auto'}`;
    }
    case 'INPUT_SPREADSHEET': {
      const header = node.config.headerRow as number | undefined;
      return `Spreadsheet · header row ${header ?? 0}`;
    }
    case 'INPUT_TEXT':
      return 'Text';
    case 'INPUT_JSON':
      return 'JSON';
    case 'INPUT_AMS_READ': {
      const ents = (node.config.entities as string[] | undefined) ?? ['account'];
      return `AMS · ${ents.join(', ')}`;
    }
    case 'INPUT_WEB_LOOKUP':
      return 'Web lookup';
    case 'EXTRACT': {
      const entities =
        (node.config.entities as { label: string; kind: string }[] | undefined) ?? [];
      if (entities.length === 0) return 'Pick what to extract';
      const builtin = entities.filter((e) => e.kind === 'builtin');
      const custom = entities.filter((e) => e.kind === 'custom').length;
      const parts = builtin.map((e) => e.label);
      if (custom > 0) parts.push(`+${custom} custom`);
      return parts.join(' · ');
    }
    case 'AGGREGATE': {
      const strategy = (node.config.strategy as string | undefined) ?? 'concat';
      if (strategy === 'dedupe' || strategy === 'merge_object') {
        return `${strategy} by ${String(node.config.keyPath ?? '—')}`;
      }
      return strategy;
    }
    case 'COMPARE': {
      const joinType = (node.config.joinType as string | undefined) ?? 'full';
      const joinKey = node.config.joinKey as string | undefined;
      return joinKey ? `${joinType} join on ${joinKey}` : `${joinType} join`;
    }
    case 'VERIFY': {
      const mode = (node.config.mode as string | undefined) ?? 'document';
      const criteria = (node.config.criteria as string[] | undefined) ?? [];
      if (criteria.length === 0) return `Mode: ${mode}`;
      return `${mode} · ${criteria.slice(0, 3).join(', ')}${criteria.length > 3 ? '…' : ''}`;
    }
    case 'OUTPUT_DOCUMENT': {
      const format = (node.config.format as string | undefined) ?? 'pdf';
      const title = node.config.title as string | undefined;
      return `${format.toUpperCase()}${title ? ` · ${title}` : ''}`;
    }
    case 'OUTPUT_INTEGRATION': {
      const type = node.config.integrationType as string | undefined;
      const actions = (node.config.actions as { capabilityKey: string }[] | undefined) ?? [];
      return `${type ?? 'Integration'} · ${actions.length} action${actions.length === 1 ? '' : 's'}`;
    }
    default:
      return null;
  }
}
